import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from '../../components/icons'
import Text from '../../elements/Typo/Text'
import Button from '../../elements/button'
import { StylesConfig } from 'react-select'
import DropDown from '../../elements/Form/dropdown'
import { ExperimentDetailsI, SharedUserResponseI, SharedUsersI, UserI } from '../../_config/types'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import { ErrorResponse, SuccessApiResponse } from '../../store/api/apiTypes'
import toast from 'react-hot-toast'
import { getShortUserName, trackEvent } from '../../_common/functions'
import { useCommonApi } from '../../store/api/hooks/commonApiHook'
import { useUserSelector } from '../../store/selectors/userSelector'
import { URLS } from '../../_config'
import ModalCloseIconButton from '../modalCloseIconButton'

interface ShareExperimentModalProps {
    _ref: any
    experiment: ExperimentDetailsI
    updateSharedUserList: (data: SharedUsersI[]) => void
    close?: () => void
}

interface OptionI {
    image: string
    label: string
    customAbbreviation: string
    value: string
}

const ShareExperimentModal: React.FC<ShareExperimentModalProps> = ({ _ref, experiment, updateSharedUserList, close }) => {
    const { t } = useTranslation()
    const sharedUserDivRef = useRef<HTMLDivElement | null>(null)
    const { getSharedUsers, postShareUsers } = useExperimentApi()
    const { getExptSharableUsers } = useCommonApi()
    const experimentAPI: any = useExperimentApi()
    const loggedinUser: any = useUserSelector()
    const [isOpen, setIsOpen] = useState<boolean>(false)

    useEffect(() => {
        const dialog = _ref?.current as HTMLDialogElement | null
        if (!dialog) return

        //const handleOpen = () => setIsOpen(true)
        const handleClose = () => {
            setSelectedOptions([])
            setSharedUserList({
                data: [],
                page: 0,
                per_page: 5,
                total_users: 0,
            })
            setIsOpen(false)
            setSharedUserList({ data: [], page: 0, per_page: 100, total_users: 0 })
            setSelectedOptions([])
            close?.()
            _ref.current?.close()
        }

        // Check initial state
        setIsOpen(dialog.open)

        dialog.addEventListener('close', handleClose)

        // Use MutationObserver to detect open attribute changes (showModal/show)
        const observer = new MutationObserver(() => {
            setIsOpen(dialog.open)
        })
        observer.observe(dialog, { attributes: true, attributeFilter: ['open'] })

        return () => {
            dialog.removeEventListener('close', handleClose)
            observer.disconnect()
        }
    }, [_ref.current])

    const [sharedUserList, setSharedUserList] = useState<SharedUserResponseI>({
        data: [],
        page: 0,
        per_page: 100,
        total_users: 0,
    })
    const [selectedOptions, setSelectedOptions] = useState<OptionI[]>([])
    const [sharedUsersExpend, setSharedUsersExpend] = useState<boolean>(false)
    const [isSetShareButtonLoader, setIsSetShareButtonLoader] = useState<boolean>(false)
    const [showShareUserLoader, setShowShareUserLoader] = useState<boolean>(false)
    const [showCopied, setShowCopied] = useState<boolean>(false)
    const [users, setUsers] = useState<any[]>([])
    const [shareURL, setShareURL] = useState<string>('')
    const [incomplete, setIncomplete] = useState<boolean | null>(null)

    const _handleCopy = (text: string) => {
        trackEvent('share_experiment_copy_link_clicked', { experiment_literal: experiment?.experiment_literal })
        navigator.clipboard
            .writeText(text)
            .then(() => {
                trackEvent('share_experiment_copy_link_success', { experiment_literal: experiment?.experiment_literal })
                setShowCopied(true)
            })
            .catch(() => {
                trackEvent('share_experiment_copy_link_failed', { experiment_literal: experiment?.experiment_literal })
            })
    }

    const getShareableUsers = () => {
        experimentAPI.shareableUserList(
            { literal: experiment.experiment_literal },
            (resp: any) => {
                console.log('Shareable Users:', resp.data)
                setUsers(getUserOptionsFromResponse(resp.data))
            },
            (error: ErrorResponse<any>) => {
                toast.error(error.message)
            },
        )
    }

    const _handleFetchSharedUsers = (page: number) => {
        if (incomplete) {
            getShareableUsers()
            //return
        }
        setShowShareUserLoader(true)
        getSharedUsers(
            {
                literal: experiment.experiment_literal,
                sort_by: '-shared_at',
                page,
                per_page: 100,
            },
            (resp: SuccessApiResponse<SharedUserResponseI>) => {
                updateSharedUserList([...sharedUserList.data, ...resp.data.data])
                setSharedUserList((prevItem: SharedUserResponseI) => {
                    return {
                        ...resp.data,
                        data: [...resp.data.data],
                    }
                })

                setShowShareUserLoader(false)
            },
            (error: ErrorResponse<SharedUserResponseI>) => {
                setShowShareUserLoader(false)
                toast.error(error.message)
            },
        )
    }

    const getUserOptionsFromResponse = (users: any) => {
        const userOptions: OptionI[] = users.map((user: UserI) => ({
            image: '',
            label: user.first_name + ' ' + user.last_name,
            customAbbreviation: getShortUserName(user.first_name, user.last_name),
            value: user.user_literal,
        }))
        return userOptions
    }

    const _handleFetchUsers = (inputValue: string): Promise<OptionI[]> => {
        return new Promise<OptionI[]>((resolve, reject) => {
            const params = {
                literal: experiment.experiment_literal,
                sort_by: '-created_at',
                page: 1,
                per_page: 100,
                zone_ids: (loggedinUser?.zones?.length && loggedinUser?.zones.map((e: any) => e?.zone_literal)) || '',

                business_function_ids: (loggedinUser?.business_functions?.length && loggedinUser?.business_functions.map((e: any) => e?.business_function_literal)) || '',
                search: inputValue || '',
            }
            getExptSharableUsers(
                params,
                (resp: SuccessApiResponse<{ data: UserI[] }>) => {
                    return resolve(getUserOptionsFromResponse(resp.data.data))
                },
                (error: ErrorResponse<SharedUserResponseI>) => {
                    toast.error(error.message)
                    return reject(new Error('No data available'))
                },
            )
        })
    }

    const _handleShareSubmit = () => {
        trackEvent('share_experiment_submitted', {
            experiment_literal: experiment?.experiment_literal,
            recipient_count: selectedOptions.length,
        })
        setIsSetShareButtonLoader(true)
        postShareUsers(
            {
                literal: experiment.experiment_literal,
                shared_with_user_id: selectedOptions.map((e: OptionI) => e.value),
                is_summary_experiment: experiment.is_draft,
            },
            (resp: SuccessApiResponse<any>) => {
                trackEvent('share_experiment_success', { experiment_literal: experiment?.experiment_literal })
                setIsSetShareButtonLoader(false)
                setSelectedOptions([])
                setSharedUserList({
                    data: [],
                    page: 0,
                    per_page: 5,
                    total_users: 0,
                })
                _handleFetchSharedUsers(1)
                _ref.current?.close()
                toast.success(t(resp.message))
            },
            (error: ErrorResponse<any>) => {
                trackEvent('share_experiment_failed', { experiment_literal: experiment?.experiment_literal, error_message: error.message })
                setIsSetShareButtonLoader(false)
                toast.error(error.message)
            },
        )
    }

    const _handleFormatOptionLabel = (option: OptionI) => (
        <div className="flex items-center gap-1 rounded-md">
            <div className="h-[1.083rem] w-[1.083rem] rounded-full bg-primary flex items-center justify-center">
                {option.image ? <img src={option.image} alt="" /> : <span className="font-bold text-[0.417rem] leading-none">{option.customAbbreviation}</span>}
            </div>
            <Text size="xs" className="font-semibold">
                {option.label}
            </Text>
        </div>
    )

    const _handleLoadOptions = (inputValue: string): Promise<OptionI[]> => {
        trackEvent('share_experiment_recipient_searched', { experiment_literal: experiment?.experiment_literal, query_length: inputValue.length })
        return new Promise<OptionI[]>((resolve, reject) => {
            _handleFetchUsers(inputValue)
                .then((resp: OptionI[]) => {
                    return resolve(resp)
                })
                .catch((err: []) => {
                    return resolve([])
                })
        })
    }

    const _handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = sharedUserDivRef.current as HTMLDivElement // Type assertion;
        if (scrollTop + clientHeight >= scrollHeight - 5 && !showShareUserLoader) {
            _handleFetchSharedUsers(sharedUserList.page + 1)
        }
    }

    const getBackgroundColor = (isDisabled: boolean, isFocused: boolean, isSelected: boolean) => {
        if (isDisabled) {
            return '#FCFCFC'
        }
        if (isFocused) {
            return '#f5e00361'
        }
        if (isSelected) {
            return '#f5e003'
        }
        return '#ffffff'
    }

    const customStyleOption: StylesConfig = {
        control: (styles) => ({
            ...styles,
            border: '1px solid #d5dad4',
            minHeight: '2.458rem',
            boxShadow: 'none',
            paddingRight: '4rem',
            ':hover': {
                border: '1px solid #d5dad4',
                boxShadow: 'none',
            },
            ':focus': {
                border: '1px solid #d5dad4',
                outline: 'none',
                boxShadow: 'none',
            },
        }),
        option: (styles, { data, isDisabled, isFocused, isSelected }) => {
            return {
                ...styles,
                backgroundColor: getBackgroundColor(isDisabled, isFocused, isSelected),
                color: '#000000',
                cursor: isDisabled ? 'not-allowed' : 'default',
                border: '1px solid #f4f4f4',
                fontSize: '0.583rem',
                padding: '0.5rem',
            }
        },
        dropdownIndicator: (base, props) => ({
            display: 'none',
        }),
    }

    useEffect(() => {
        if (experiment && isOpen) {
            if ([200, 300, 401, 402].includes(Number(experiment.status))) {
                setIncomplete(false)
            } else {
                setIncomplete(true)
            }
            if (experiment.step < 5) {
                setShareURL(globalThis.location.origin + URLS.CREATE_EXPERIMENT(experiment.experiment_literal))
            } else if (experiment.step < 6) {
                setShareURL(globalThis.location.origin + URLS.MEASUREMENT(experiment.experiment_literal, 'data'))
            } else {
                setShareURL(globalThis.location.origin + URLS.EXPERIMENT_DETAILS(experiment.experiment_literal))
            }
            if (typeof incomplete === 'boolean') {
                _handleFetchSharedUsers(1)
            }
        }
    }, [experiment, incomplete, isOpen])

    useEffect(() => {
        if (showCopied) {
            setTimeout(() => {
                setShowCopied(false)
            }, 2000)
        }
    }, [showCopied])

    const checkUser = () => {
        if (sharedUsersExpend && sharedUserList.data.length == 0) {
            return (
                <div>
                    <Text size="sm" className="text-center font-medium">
                        {t('No User Found!')}
                    </Text>
                </div>
            )
        } else {
            return null
        }
    }

    return (
        <dialog ref={_ref} className="modal">
            {experiment && (
                <div className="modal-box rounded-[0.833rem] w-lg max-w-xl flex flex-col gap-3">
                    <ModalCloseIconButton
                        onClick={() => {
                            trackEvent('know_kpi_modal_closed', {})
                            _ref.current.close()
                        }}
                    />
                    <div className="flex flex-col justify-center items-center">
                        <Text size="xl" className="font-bold">
                            {t('Share Experiment')}
                        </Text>
                        <Text size="md" className="font-semibold">
                            {t('Please use the following link to share the experiment with other users.')}
                        </Text>
                    </div>
                    <div className="bg-[#F4F4F4] rounded px-4 py-2 flex justify-between">
                        <Text className="!text-[#808E96] text-[0.7rem] mt-1.5">{shareURL}</Text>
                        <div className="tooltip tooltip-top tooltip-info text-xs max-w-xs flex items-center" data-tip={showCopied ? t('Copied') : t('Copy to clipboard')}>
                            {showCopied ? (
                                <Icon name="tickMark" className="text-lime-600" size="1.417rem"></Icon>
                            ) : (
                                <Icon
                                    name="copy"
                                    size="1.417rem"
                                    color="black"
                                    className="cursor-pointer"
                                    onClick={() => {
                                        _handleCopy(shareURL)
                                    }}
                                ></Icon>
                            )}
                        </div>
                    </div>

                    <hr />

                    <div className="gap-3 flex flex-col">
                        <div className="">
                            <Text size="md" className="font-medium">
                                {t('This experiment is automatically available to Business Owners aligned to its zone and business function. Add additional users below to extend access and collaboration.')}
                            </Text>
                        </div>

                        <div className="relative flex justify-between">
                            {incomplete ? (
                                <DropDown
                                    placeholder={t('Select user')}
                                    isClearable={false}
                                    isMulti={true}
                                    customStyleOption={customStyleOption}
                                    formatOptionLabel={_handleFormatOptionLabel}
                                    className="w-full"
                                    options={users}
                                    value={selectedOptions}
                                    onChange={(event: OptionI[]) => {
                                        if (event.length > selectedOptions.length) {
                                            trackEvent('share_experiment_recipient_selected', { experiment_literal: experiment?.experiment_literal, recipient_count: event.length })
                                        } else {
                                            trackEvent('share_experiment_recipient_removed', { experiment_literal: experiment?.experiment_literal, recipient_count: event.length })
                                        }
                                        setSelectedOptions(event)
                                    }}
                                />
                            ) : (
                                <DropDown
                                    async={true}
                                    placeholder={t('Select user')}
                                    isClearable={false}
                                    isMulti={true}
                                    asyncLoadOptions={_handleLoadOptions}
                                    asyncFormatOptionLabel={_handleFormatOptionLabel}
                                    customStyleOption={customStyleOption}
                                    className="w-full"
                                    value={selectedOptions}
                                    onChange={(event: OptionI[]) => {
                                        if (event.length > selectedOptions.length) {
                                            trackEvent('share_experiment_recipient_selected', { experiment_literal: experiment?.experiment_literal, recipient_count: event.length })
                                        } else {
                                            trackEvent('share_experiment_recipient_removed', { experiment_literal: experiment?.experiment_literal, recipient_count: event.length })
                                        }
                                        setSelectedOptions(event)
                                    }}
                                />
                            )}

                            {isSetShareButtonLoader ? (
                                <div className="absolute  right-[0.5rem] mt-[0.8rem]">
                                    <Icon name="buttonLoader"></Icon>
                                </div>
                            ) : (
                                <div className="absolute right-[0.25rem] mt-[0.4rem]">
                                    <Button
                                        size="xxs"
                                        theme="secondary"
                                        className="rounded-[0.3rem]"
                                        disabled={!selectedOptions.length}
                                        onClick={() => {
                                            if (!isSetShareButtonLoader && selectedOptions.length) {
                                                _handleShareSubmit()
                                            }
                                        }}
                                    >
                                        {t('Share')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                    <hr />
                    <div className="min-h-[10rem] gap-3 flex flex-col">
                        <div>
                            <div className="flex justify-between">
                                <div className="flex">
                                    <Text size="sm">{t('Experiment is shared with')}</Text>
                                    &nbsp;
                                    <Text size="sm" className="font-semibold">
                                        ({sharedUserList.total_users} {t('Members')})
                                    </Text>
                                </div>
                                <Icon
                                    name={sharedUsersExpend ? 'down' : 'up'}
                                    onClick={() => {
                                        if (sharedUsersExpend) {
                                            trackEvent('share_experiment_existing_users_collapsed', { experiment_literal: experiment?.experiment_literal })
                                        } else {
                                            trackEvent('share_experiment_existing_users_expanded', { experiment_literal: experiment?.experiment_literal })
                                        }
                                        setSharedUsersExpend(!sharedUsersExpend)
                                    }}
                                ></Icon>
                            </div>
                            {sharedUserList.data.length ? (
                                <div className="max-h-[10rem] overflow-y-auto" ref={sharedUserDivRef} onScroll={_handleScroll}>
                                    {sharedUserList.data.map((user: SharedUsersI, index: number) => (
                                        <div key={`user_${user.first_name}`}>
                                            <div className={`flex justify-between py-2 ${index + 1 === sharedUserList.data.length ? '' : 'border-b'}`}>
                                                <div className="flex items-center gap-1">
                                                    <div className="h-[1.667rem] w-[1.667rem] rounded-full bg-primary flex items-center justify-center">
                                                        {user.image ? (
                                                            <img src={user.image} alt="" />
                                                        ) : (
                                                            <Text size="md" className="font-bold">
                                                                {getShortUserName(user.first_name, user.last_name)}
                                                            </Text>
                                                        )}
                                                    </div>
                                                    <Text size="md" className="font-semibold">
                                                        {user.first_name}&nbsp;
                                                        {user.last_name}
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                checkUser()
                            )}
                        </div>
                        <hr />
                        <p className="text-[0.6rem] leading-[1.35] rounded-md bg-gray-200 p-1.5">
                            <strong>{t('Important')}:</strong> {t('Sharing this experiment grants the recipient the ability to manage and make changes to it. Please ensure they have the appropriate knowledge and authority before proceeding.')}
                        </p>
                        <hr />
                        {!incomplete && (
                            <div className="flex flex-col items-center gap-1">
                                <Button
                                    size="sm"
                                    className="bg-gradient-to-b from-[#E5B611] to-[#F5E003]"
                                    onClick={() => {
                                        trackEvent('share_experiment_pdf_opened', { experiment_literal: experiment?.experiment_literal })
                                        globalThis.open(URLS.EXPERIMENT_DETAILS_DOWNLOAD(experiment.experiment_literal), '_blank')
                                    }}
                                >
                                    <Icon name="download-alt"></Icon>
                                    <Text>{t('Download Result as a PDF')}</Text>
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <form method="dialog" className="modal-backdrop">
                <button
                    type="button"
                    onClick={() => {
                        trackEvent('share_experiment_modal_closed', { experiment_literal: experiment?.experiment_literal })
                        close?.()
                        _ref.current?.close()
                    }}
                >
                    {t('Close')}
                </button>
            </form>
        </dialog>
    )
}

export default ShareExperimentModal
